import type { ReactNode } from "react";
import { strings } from "@/I18n";
import { cn } from "@/lib/utils";
import { TEST_IDS } from "@/v2/constants/testIds";
import { useIsMobile } from "@/v2/hooks/useMediaQuery";
import { Button } from "@/v2/ui/primitives/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/v2/ui/primitives/dialog";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/v2/ui/primitives/sheet";

export interface ResponsiveModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: ReactNode;
  children: ReactNode;
  maxWidth?: string;
  className?: string;
  showDragHandle?: boolean;

  // Standardized footer options (mutually exclusive with `footer`)
  onCancel?: () => void;
  onConfirm?: () => void;
  onReset?: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  resetLabel?: string;
  confirmVariant?: "default" | "destructive";
  confirmDisabled?: boolean;
  resetDisabled?: boolean;
  loading?: boolean;
  formId?: string; // For form submission - renders submit button instead of confirm

  // Custom footer escape hatch (takes precedence)
  footer?: ReactNode;

  /**
   * When false, overlay clicks and ESC will not close the modal. The X close
   * button and any explicit cancel/confirm action still work. Use for modals
   * that present one-shot information where an accidental dismiss is costly.
   * Defaults to true.
   */
  dismissable?: boolean;
  onCloseAutoFocus?: (event: Event) => void;
}

export function ResponsiveModal({
  open,
  onOpenChange,
  title,
  children,
  maxWidth = "tw-max-w-lg",
  className,
  showDragHandle = true,
  onCancel,
  onConfirm,
  onReset,
  confirmLabel,
  cancelLabel,
  resetLabel,
  confirmVariant = "default",
  confirmDisabled = false,
  resetDisabled = false,
  loading = false,
  formId,
  footer,
  dismissable = true,
  onCloseAutoFocus,
}: ResponsiveModalProps) {
  const isMobile = useIsMobile();

  const blockedDismissHandlers = dismissable
    ? {}
    : {
        onPointerDownOutside: (e: Event) => e.preventDefault(),
        onEscapeKeyDown: (e: KeyboardEvent) => e.preventDefault(),
      };

  const renderFooter = () => {
    if (footer) return footer;
    if (!onCancel && !onConfirm && !onReset && !formId) return null;

    return (
      <>
        {onReset && (
          <Button
            variant="outline"
            onClick={onReset}
            disabled={resetDisabled || loading}
          >
            {resetLabel || strings("common.reset")}
          </Button>
        )}
        {onCancel && (
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={loading}
            data-testid={TEST_IDS.modalCancel}
          >
            {cancelLabel || strings("common.cancel")}
          </Button>
        )}
        {formId && (
          <Button
            type="submit"
            form={formId}
            disabled={confirmDisabled || loading}
            data-testid={TEST_IDS.modalConfirm}
          >
            {loading
              ? strings("common.loading")
              : confirmLabel || strings("common.save")}
          </Button>
        )}
        {onConfirm && !formId && (
          <Button
            variant={confirmVariant}
            onClick={onConfirm}
            disabled={confirmDisabled || loading}
            data-testid={TEST_IDS.modalConfirm}
          >
            {loading
              ? strings("common.loading")
              : confirmLabel || strings("common.confirm")}
          </Button>
        )}
      </>
    );
  };

  const footerContent = renderFooter();

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          className={cn(
            "tw-rounded-t-xl tw-max-h-[90dvh] tw-flex tw-flex-col tw-px-4 tw-pt-3 tw-pb-4",
            className
          )}
          {...blockedDismissHandlers}
          onCloseAutoFocus={onCloseAutoFocus}
        >
          {showDragHandle && (
            <div className="tw-mx-auto tw-mb-1 tw-h-1 tw-w-10 tw-rounded-full tw-bg-muted tw-flex-shrink-0" />
          )}
          {title && (
            <SheetHeader className="tw-flex-shrink-0 tw-pb-1">
              <SheetTitle className="tw-text-base">{title}</SheetTitle>
            </SheetHeader>
          )}
          <div className="tw-flex-1 tw-overflow-y-auto tw-py-1">{children}</div>
          {footerContent && (
            <SheetFooter className="tw-flex-shrink-0 tw-flex tw-gap-2 tw-pt-2 tw-border-t">
              {footerContent}
            </SheetFooter>
          )}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          maxWidth,
          "tw-max-h-[90dvh] tw-overflow-y-auto",
          className
        )}
        {...blockedDismissHandlers}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        {title && (
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
        )}
        {children}
        {footerContent && (
          <DialogFooter className="tw-gap-2 tw-pt-4">
            {footerContent}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
